import React from 'react';
import {useParams} from 'react-router';
import {useMutation, useQuery} from '@apollo/client';
import gql from 'graphql-tag';
import ReactDocumentTitle from '../../../components/ReactDocumentTitle';
import AppLayout from '../../../layouts/AppLayout';
import BackLink from '../../../ui/BackLink';
import {handleError, handleSuccess} from '../../../utils/Apollo';
import FormTitle from '../../../ui/FormTitle';
import Preloader from '../../../ui/Preloader';
import {reverse} from '../../../urls';
import Menu from '../../../components/licenseType/Menu';
import FormWrapper from '../../../components/licenseType/FormWrapper';
import BackForward from '../../../components/licenseType/BackForward';
import {LicenseType} from '../../../schema';
import RenewalPeriodForm from '../../../components/licenseType/RenewalPeriodForm';

interface PathParams {
  id: string
}

interface RenewalPeriodQuery {
  licenseType: LicenseType
}

export default function EditRenewalPeriodScene() {
  const {id} = useParams<PathParams>();
  const {loading, data, error} = useQuery<RenewalPeriodQuery>(query, {
    variables: {id},
    errorPolicy: 'all'
  });
  const [submit] = useMutation(mutation);
  const backLink = reverse('brand_settings_license_types');

  const onFormSubmit = (period: number) => {
    submit({
      variables: {period, id}
    }).then(
      () => handleSuccess('Renewal period updated', backLink),
      (apolloError) => handleError(apolloError)
    );
  }

  return (
    <>
      <ReactDocumentTitle title="Edit renewal period"/>
      <AppLayout className="bg-gray-light">
        <BackLink backLink={reverse('brand_settings_license_types')} title="Back to settings"/>
        {error && <Preloader/>}
        {loading && <Preloader/>}
        {!loading && !error &&
          <>
            <Menu activeIndex={4}/>
            <FormWrapper>
              <FormTitle>Set renewal period</FormTitle>
              <RenewalPeriodForm backLink={reverse('brand_settings_license_types')}
                                 renewalPeriod={data?.licenseType.renewalPeriod} onSubmit={onFormSubmit}/>
              {data?.licenseType.agreement?.id &&
                <BackForward back={{
                  url: reverse('license_agreement_edit', {id: data?.licenseType.agreement?.id, licenseTypeId: id}),
                  label: 'Edit Agreement'
                }}
                             forward={{
                               url: reverse('brand_settings_license_types'),
                               label: 'To settings'
                             }}/>
              }
            </FormWrapper>
          </>
        }
      </AppLayout>
    </>
  );
}

const query = gql`
  query EditRenewalPeriodQuery($id: ID!) {
    licenseType(id: $id) {
      name
      renewalPeriod
      agreement {
        id
      }
    }
  }
`;

const mutation = gql`
  mutation UpdateFormMutation($id: ID!, $period: Int!) {
    updateRenewalPeriod(id: $id, period: $period) {
      id
      renewalPeriod
    }
  }
`;
